/**
 * ComplianceSection - Security & regulatory compliance
 * Builds trust with medical professionals: HIPAA, GDPR, encryption
 */
import React from 'react';
import { Typography, Box, Container } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppGoodIcon from '@mui/icons-material/GppGood';
import PolicyIcon from '@mui/icons-material/Policy';
import StorageIcon from '@mui/icons-material/Storage';

import { COLORS, TYPOGRAPHY, GRADIENTS, TRANSITIONS, RADIUS } from './landingTheme';
import { ScrollReveal, StaggerReveal } from './animations';

const COMPLIANCE_ITEMS = [
  {
    icon: <ShieldIcon />,
    title: 'HIPAA',
    subtitle: 'Compliant',
    description: 'Cumplimos con los estándares de protección de datos de salud de Estados Unidos.',
    color: '#0ea5e9',
  },
  {
    icon: <VerifiedUserIcon />,
    title: 'GDPR',
    subtitle: 'Ready',
    description: 'Preparados para el Reglamento General de Protección de Datos de la Unión Europea.',
    color: '#8b5cf6',
  },
  {
    icon: <LockIcon />,
    title: 'Encriptación',
    subtitle: 'End-to-End',
    description: 'Todos los datos en tránsito y en reposo están protegidos con cifrado AES-256.',
    color: '#10b981',
  },
  {
    icon: <GppGoodIcon />,
    title: 'Consentimiento',
    subtitle: 'Explícito',
    description: 'Cada paciente autoriza el uso de sus datos antes de interactuar con el asistente.',
    color: '#f59e0b',
  },
  {
    icon: <PolicyIcon />,
    title: 'Ley 1581',
    subtitle: 'Colombia',
    description: 'Cumplimiento con la ley de protección de datos personales de Colombia y LFPDPPP de México.',
    color: '#ec4899',
  },
  {
    icon: <StorageIcon />,
    title: 'Retención',
    subtitle: 'Inteligente',
    description: 'Los datos se eliminan automáticamente según políticas de retención configurables.',
    color: '#6366f1',
  },
];

const ComplianceCard = ({ item }) => (
  <Box
    sx={{
      p: { xs: 2.5, md: 3 },
      borderRadius: RADIUS.xl,
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.8)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      transition: TRANSITIONS.normal,
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 32px ${item.color}15`,
        borderColor: `${item.color}30`,
      },
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '14px',
        background: `${item.color}10`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
        '& svg': { fontSize: 24, color: item.color },
      }}
    >
      {item.icon}
    </Box>

    {/* Title + subtitle */}
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mb: 0.8 }}>
      <Typography
        sx={{
          fontSize: '1.1rem',
          fontWeight: 800,
          color: COLORS.textPrimary,
        }}
      >
        {item.title}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: item.color,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {item.subtitle}
      </Typography>
    </Box>

    {/* Description */}
    <Typography
      sx={{
        fontSize: TYPOGRAPHY.small,
        color: COLORS.textSecondary,
        lineHeight: 1.65,
      }}
    >
      {item.description}
    </Typography>
  </Box>
);

export default function ComplianceSection() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, ${COLORS.bgGray} 0%, #f0f4f8 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '-5%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(14, 165, 233, 0.05) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '-5%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.04) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            {/* Shield badge */}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0f2440 0%, #1e3a5f 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2.5,
                boxShadow: '0 8px 24px rgba(15, 36, 64, 0.25)',
              }}
            >
              <ShieldIcon sx={{ fontSize: 28, color: '#fbbf24' }} />
            </Box>

            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: COLORS.accent,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              Seguridad y cumplimiento
            </Typography>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.h2,
                fontWeight: 800,
                color: COLORS.textPrimary,
                letterSpacing: '-0.02em',
                mb: 1.5,
              }}
            >
              Tus datos médicos,{' '}
              <Box
                component="span"
                sx={{
                  background: GRADIENTS.textGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                protegidos
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.body,
                color: COLORS.textSecondary,
                maxWidth: 540,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              La privacidad del paciente es nuestra prioridad. Cumplimos con las regulaciones
              internacionales más estrictas de protección de datos médicos.
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Cards grid */}
        <StaggerReveal
          staggerDelay={0.08}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: { xs: 2, md: 3 },
            maxWidth: 960,
            mx: 'auto',
          }}
        >
          {COMPLIANCE_ITEMS.map((item, idx) => (
            <ComplianceCard key={idx} item={item} />
          ))}
        </StaggerReveal>
      </Container>
    </Box>
  );
}
